import type { Minor } from '@/domain/money'
import { subtotalMinor } from '@/domain/money'
import { UNTAGGED_CURRENCY } from '@/domain/rollup'
import { PRICES_SOURCE_ID } from '@/domain/valuation'
import type { HoldingRow, HoldingsResponse, SourceState, UnpricedHolding } from '~/api/client'
import { daysBetween } from '~/state/dates'

export type SourceStatus = SourceState['status']

export type SourceTone = 'failed' | 'stale' | 'healthy' | 'idle'

export interface SourceHealth {
  source: string
  status: SourceStatus
  tone: SourceTone
  /** The last day this source reported for itself, rather than being repeated. */
  lastCollected: string | null
  staleDays: number | null
  error: string | null
  accounts: number
  positions: number
  projected: number
  unpriced: number
  /**
   * What the source's priced holdings come to — null where none of them are
   * priced, because a source that reported positions nobody can value has not
   * contributed zero.
   */
  valueMinor: Minor | null
  summary: string
}

export interface HealthReport {
  date: string | null
  baseCurrency: string
  sources: SourceHealth[]
  counts: Record<SourceTone, number>
  attention: SourceHealth[]
  /** How many holdings are in no figure on the page, across every source. */
  unpriced: number
  /** Which holdings that count is, and why each one — the answer to "which ones?" */
  unpricedHoldings: UnpricedHolding[]
  missingPrices: string[]
  /**
   * Pairs with two real currencies and no rate between them. A holding priced
   * in one of these is left out of the totals, which is what the page says
   * about them — so the pair standing in for a basis nobody dated to a currency
   * is kept out of this list and answered separately, because that holding is
   * still counted in full.
   */
  missingFx: string[]
  /** Whether some cost basis reached the totals without saying what it was paid in. */
  untaggedCostBasis: boolean
  carriedFx: string[]
  projectedMinor: Minor
  /**
   * What the feed said when the last run's pricing failed, in its own words —
   * or null on a night it answered. Not a source: it collects nothing of
   * anybody's, so it is kept out of the table above and answered on its own
   * rather than folded into a row built for a bank or a broker.
   */
  priceRunError: string | null
}

const TONE_OF: Record<SourceStatus, SourceTone> = {
  error: 'failed',
  carried_forward: 'stale',
  ok: 'healthy',
  not_configured: 'idle',
}

const TONE_ORDER: SourceTone[] = ['failed', 'stale', 'healthy', 'idle']

export function deriveHealth(holdings: HoldingsResponse): HealthReport {
  const rowsBySource = groupBySource(holdings.rows)
  const priceRun = holdings.sources.find((s) => s.source === PRICES_SOURCE_ID)
  const names = new Set(
    [...holdings.sources.map((s) => s.source), ...rowsBySource.keys()].filter(
      (source) => source !== PRICES_SOURCE_ID,
    ),
  )
  const stateOf = new Map(holdings.sources.map((s) => [s.source, s]))

  const sources = [...names]
    .map((source) => {
      const rows = rowsBySource.get(source) ?? []
      return healthOf(source, stateOf.get(source), rows, holdings.date)
    })
    .sort(
      (a, b) =>
        TONE_ORDER.indexOf(a.tone) - TONE_ORDER.indexOf(b.tone) ||
        a.source.localeCompare(b.source),
    )

  const counts: Record<SourceTone, number> = { failed: 0, stale: 0, healthy: 0, idle: 0 }
  for (const source of sources) counts[source.tone] += 1

  return {
    date: holdings.date,
    baseCurrency: holdings.baseCurrency,
    sources,
    counts,
    attention: sources.filter((s) => s.tone === 'failed' || s.tone === 'stale'),
    unpriced: sources.reduce((count, source) => count + source.unpriced, 0),
    unpricedHoldings: holdings.unpriced ?? [],
    missingPrices: holdings.missingPrices,
    missingFx: holdings.missingFx.filter((pair) => !isUntaggedBasisPair(pair)),
    untaggedCostBasis: holdings.missingFx.some(isUntaggedBasisPair),
    carriedFx: holdings.carriedFx,
    projectedMinor: holdings.totals.projectedMinor,
    priceRunError: priceRun?.status === 'error' ? (priceRun.error ?? null) : null,
  }
}

function isUntaggedBasisPair(pair: string): boolean {
  return pair.startsWith(`${UNTAGGED_CURRENCY}/`)
}

function healthOf(
  source: string,
  state: SourceState | undefined,
  rows: readonly HoldingRow[],
  date: string | null,
): SourceHealth {
  const accounts = new Set(rows.map((row) => row.accountId)).size
  const status = state?.status ?? inferStatus(latestAsOf(rows), date)
  const lastCollected = lastCollectionOf(status, state, rows)
  // One pass for the figure and for what it leaves out, so the two cannot
  // disagree about which rows are priced.
  const contributed = subtotalMinor(rows.map((row) => row.valueMinor))

  return {
    source,
    status,
    tone: TONE_OF[status],
    lastCollected,
    staleDays: date !== null && lastCollected !== null ? daysBetween(lastCollected, date) : null,
    error: state?.error ?? null,
    accounts,
    positions: rows.length,
    projected: rows.filter((row) => !row.confirmed).length,
    unpriced: contributed.absent,
    valueMinor: contributed.valueMinor,
    summary: summarise(rows.length, accounts),
  }
}

/**
 * Carrying positions forward dates them at the day being valued, so the rows of
 * a failing source all claim to be current. `stale_since` is the day it last
 * spoke for itself, and that is the date worth showing.
 */
function lastCollectionOf(
  status: SourceStatus,
  state: SourceState | undefined,
  rows: readonly HoldingRow[],
): string | null {
  if (status === 'carried_forward') {
    return state?.staleSince ?? latestAsOf(rows.filter((row) => row.confirmed))
  }
  return latestAsOf(rows)
}

function latestAsOf(rows: readonly HoldingRow[]): string | null {
  return rows.reduce<string | null>(
    (latest, row) => (latest === null || row.asOf > latest ? row.asOf : latest),
    null,
  )
}

/**
 * A source can hold positions without a run record — manual entry writes rows
 * nobody scheduled. The positions are the evidence that it reported, so the
 * absence of a log entry is not read as an absence of data.
 */
function inferStatus(lastCollected: string | null, date: string | null): SourceStatus {
  if (lastCollected === null) return 'not_configured'
  return date === null || lastCollected >= date ? 'ok' : 'carried_forward'
}

function summarise(positions: number, accounts: number): string {
  if (positions === 0) return 'nothing'
  return `${plural(positions, 'position')} in ${plural(accounts, 'account')}`
}

function plural(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? '' : 's'}`
}

function groupBySource(rows: readonly HoldingRow[]): Map<string, HoldingRow[]> {
  const bySource = new Map<string, HoldingRow[]>()
  for (const row of rows) {
    const list = bySource.get(row.source)
    if (list) list.push(row)
    else bySource.set(row.source, [row])
  }
  return bySource
}
